import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const { FiDollarSign, FiUsers, FiShare2, FiCheck, FiClock, FiExternalLink, FiCopy } = FiIcons;

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

export default function Affiliate() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Onboarding form state
  const [payoutMethod, setPayoutMethod] = useState('usdt');
  const [payoutDestination, setPayoutDestination] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const loadSummary = async () => {
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase.rpc('get_my_affiliate_summary');
      if (e) throw e;
      setSummary(data);
      if (data?.payout_method) setPayoutMethod(data.payout_method);
      if (data?.payout_destination) setPayoutDestination(data.payout_destination);
    } catch (err) {
      setError(err.message || 'Failed to load affiliate summary');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) loadSummary(); }, [user?.id]);

  const handleOptIn = async (e) => {
    e.preventDefault();
    if (!acceptTerms || !acceptDisclaimer) {
      setError('You must accept both the terms and the no-financial-claims clause to activate.');
      return;
    }
    if (payoutDestination.trim().length < 5) {
      setError('Enter a valid USDT wallet address or PayPal email.');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const { error: e } = await supabase.rpc('accept_affiliate_terms', {
        p_payout_method: payoutMethod,
        p_payout_destination: payoutDestination.trim(),
      });
      if (e) throw e;
      await loadSummary();
    } catch (err) {
      setError(err.message || 'Failed to activate');
    } finally { setSubmitting(false); }
  };

  const copyLink = () => {
    if (!summary?.share_code) return;
    const link = `https://snap-chart.ai/?ref=${summary.share_code}`;
    try {
      navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch { /* noop */ }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        Please <Link to="/login" className="text-cyan-400 ml-2 mr-2">log in</Link> to access the affiliate program.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        Loading affiliate dashboard…
      </div>
    );
  }

  const isActive = summary?.affiliate_status === 'active';
  const shareLink = summary?.share_code ? `https://snap-chart.ai/?ref=${summary.share_code}` : 'https://snap-chart.ai/?ref=...';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <SafeIcon icon={FiDollarSign} className="text-cyan-400 w-4 h-4" />
            <span className="text-xs uppercase tracking-wider text-cyan-400 font-medium">Affiliate Program</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-3">Earn 30% recurring</h1>
          <p className="text-gray-400 max-w-2xl">
            On every subscription you refer to SnapChart AI. Paid monthly in USDT or PayPal. No cap, no expiry.
          </p>
        </motion.div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-300 text-sm">{error}</div>
        )}

        {/* ─────────── ACTIVATED: dashboard ─────────── */}
        {isActive && (
          <>
            {/* Earnings grid */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard icon={FiDollarSign} label="Pending balance" value={fmtUsd(summary.pending_balance_usd)} highlight />
              <StatCard icon={FiCheck}     label="Paid lifetime"   value={fmtUsd(summary.paid_total_usd)} />
              <StatCard icon={FiClock}     label="Last 30 days"    value={fmtUsd(summary.last_30d_commission_usd)} />
              <StatCard icon={FiUsers}     label="Paying referrals" value={summary.referred_paying_users || 0} />
            </motion.div>

            {/* Share link card */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
              <div className="bg-gradient-to-br from-cyan-900/30 to-purple-900/20 border border-cyan-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-cyan-400 mb-3">
                  <SafeIcon icon={FiShare2} className="w-4 h-4" /> Your unique link
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <code className="flex-grow px-4 py-3 bg-gray-900/70 border border-white/10 rounded-lg text-cyan-400 font-mono text-sm break-all">{shareLink}</code>
                  <button onClick={copyLink} className="px-5 py-3 bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 rounded-lg font-medium hover:bg-cyan-500/30 flex items-center justify-center gap-2 whitespace-nowrap">
                    <SafeIcon icon={FiCopy} /> {linkCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Every signup that comes through this link is credited to you for life. Earnings accrue automatically the moment they pay their first invoice.
                </p>
              </div>
            </motion.div>

            {/* Payout info */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-white">Payout method</h3>
                  <button onClick={() => setError(null) || setAcceptTerms(false) || setAcceptDisclaimer(false)} className="text-xs text-cyan-400 hover:text-cyan-300">Edit</button>
                </div>
                <div className="text-sm">
                  <div className="flex justify-between py-1.5 border-b border-white/5">
                    <span className="text-gray-400">Method</span>
                    <span className="text-white font-medium uppercase">{summary.payout_method}</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-white/5">
                    <span className="text-gray-400">Destination</span>
                    <span className="text-white font-mono text-xs break-all">{summary.payout_destination}</span>
                  </div>
                  <div className="flex justify-between py-1.5">
                    <span className="text-gray-400">Terms accepted</span>
                    <span className="text-white">{fmtDate(summary.terms_accepted_at)}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Payout history */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-10">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-emerald-400" /> Payout history</h3>
                {!summary.recent_payouts || summary.recent_payouts.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">No payouts yet. Your first batch will be issued at the end of the current month if your pending balance is &gt;= $20.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-white/5">
                          <th className="py-2">Period</th>
                          <th className="py-2 text-right">Amount</th>
                          <th className="py-2">Method</th>
                          <th className="py-2">Tx</th>
                          <th className="py-2">Paid at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.recent_payouts.map((p, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="py-2 text-gray-200">{p.period_month}</td>
                            <td className="py-2 text-right text-emerald-400 font-medium tabular-nums">{fmtUsd(p.total_commission_usd)}</td>
                            <td className="py-2 text-gray-400 uppercase text-xs">{p.payout_method}</td>
                            <td className="py-2 text-gray-500 font-mono text-xs">{p.tx_hash ? p.tx_hash.slice(0, 12) + '…' : '—'}</td>
                            <td className="py-2 text-gray-400 text-xs">{fmtDate(p.paid_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}

        {/* ─────────── NOT YET ACTIVATED: onboarding form ─────────── */}
        {!isActive && (
          <motion.form onSubmit={handleOptIn} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white">Activate your affiliate account</h2>
              <p className="text-sm text-gray-400">Two minutes. Choose your payout method, accept the terms, and start earning.</p>
            </div>

            {/* Payout method */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">Payout method</label>
              <div className="grid grid-cols-2 gap-3">
                {['usdt', 'paypal'].map((m) => (
                  <button key={m} type="button" onClick={() => setPayoutMethod(m)}
                    className={`px-4 py-3 rounded-lg border text-sm font-medium transition ${payoutMethod === m ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300' : 'bg-gray-900/50 border-white/10 text-gray-400 hover:border-white/20'}`}>
                    {m === 'usdt' ? 'USDT (TRC-20 / ERC-20)' : 'PayPal'}
                  </button>
                ))}
              </div>
            </div>

            {/* Payout destination */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {payoutMethod === 'usdt' ? 'USDT wallet address' : 'PayPal email'}
              </label>
              <input type="text" value={payoutDestination} onChange={(e) => setPayoutDestination(e.target.value)}
                placeholder={payoutMethod === 'usdt' ? 'e.g. TXyz...QwErT' : 'you@example.com'}
                className="w-full px-4 py-3 bg-gray-900/70 border border-white/10 rounded-lg text-white text-sm font-mono focus:border-cyan-500/50 focus:outline-none" />
              <p className="text-xs text-gray-500 mt-2">
                {payoutMethod === 'usdt' ? 'Double-check the network (TRC-20 recommended for low fees). Lost funds from wrong addresses cannot be recovered.' : 'PayPal will be used as the recipient email for monthly payouts. Make sure your account can receive USD.'}
              </p>
            </div>

            {/* Terms checkboxes */}
            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-1" />
                <span className="text-sm text-gray-300">
                  I have read and accept the <Link to="/affiliate/terms" target="_blank" className="text-cyan-400 hover:underline">Affiliate Program Terms</Link>, including the 30% recurring commission structure, monthly payout schedule, and program rules.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={acceptDisclaimer} onChange={(e) => setAcceptDisclaimer(e.target.checked)} className="mt-1" />
                <span className="text-sm text-gray-300">
                  I agree <strong>not to make financial-performance claims or guarantees about trading outcomes</strong> when promoting SnapChart AI. This is a software referral program — commissions are paid on SaaS subscriptions only, not on trading results.
                </span>
              </label>
            </div>

            <button type="submit" disabled={submitting || !acceptTerms || !acceptDisclaimer}
              className="w-full px-6 py-3 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 text-cyan-300 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {submitting ? 'Activating…' : <>Activate my affiliate account <SafeIcon icon={FiExternalLink} /></>}
            </button>
          </motion.form>
        )}

        {/* Always-on legal footer */}
        <p className="text-xs text-gray-600 mt-10 text-center max-w-3xl mx-auto">
          This is a software referral program. Commissions are paid on SaaS subscriptions only — not on trading results. <Link to="/affiliate/terms" className="text-cyan-400 hover:underline">Read the full terms</Link>. Not financial advice.
        </p>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, highlight }) {
  return (
    <div className={`relative overflow-hidden rounded-xl border p-4 ${highlight ? 'border-cyan-500/30 bg-cyan-900/10' : 'border-white/10 bg-white/5'}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-400 mb-2">
        <SafeIcon icon={icon} className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${highlight ? 'text-cyan-400' : 'text-white'}`}>{value}</div>
    </div>
  );
}
